import { useEffect, useState } from "react";
import { fetchMyReferralStats, getMyReferralCode } from "../referral";
import { Copy, Check, Users, DollarSign, MousePointerClick } from "lucide-react";
import { toast } from "sonner";
import { useI18n, useL } from "../i18n";
import SocialShare from "./SocialShare";

export default function ReferralPanel() {
  const { t, lang } = useI18n();
  const L = useL();
  const [stats, setStats] = useState({ clicks: 0, estimated_bookings: 0, estimated_commission_usd: 0 });
  const [copied, setCopied] = useState(false);
  const code = getMyReferralCode();
  const refUrl = typeof window !== "undefined" ? `${window.location.origin}/?ref=${code}` : "";

  useEffect(() => {
    fetchMyReferralStats().then(setStats).catch((err) => console.warn("referral stats failed", err));
  }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(refUrl);
      setCopied(true);
      toast.success(L({ ar: "تم النسخ", en: "Copied", fr: "Copié" }));
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };

  const shareText = L({
    ar: `اكتشف الشرق الأوسط مع رحّال — دليل السفر الذكي مع أسعار حقيقية ومساعد AI 🌍`,
    en: `Discover the Middle East with Rahhal — smart travel guide with real prices + AI concierge 🌍`,
    fr: `Découvrez le Moyen-Orient avec Rahhal — guide voyage avec prix réels + concierge IA 🌍`
  });

  return (
    <div className="rounded-3xl bg-white border border-[#E6DED3] p-6 space-y-5" data-testid="referral-panel">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-[#C85A32] font-semibold">
          {L({ ar: "برنامج الإحالة", en: "Referral program", fr: "Programme de parrainage" })}
        </div>
        <h3 className="mt-1 font-display text-xl text-[#1C1917]">
          {L({ ar: "اكسب مع كل صديق تدعوه", en: "Earn from every friend you invite", fr: "Gagnez à chaque ami invité" })}
        </h3>
        <p className="mt-1 text-sm text-[#57534E]">
          {L({
            ar: "شارك رابطك الفريد. عندما يحجز أحد أصدقائك فندقاً عبر رحّال، تحصل على عمولة تقديرية.",
            en: "Share your unique link. When a friend books a hotel through Rahhal, you earn an estimated commission.",
            fr: "Partagez votre lien unique. Lorsqu'un ami réserve via Rahhal, vous gagnez une commission estimée."
          })}
        </p>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <StatBox icon={MousePointerClick} value={stats.clicks} label={L({ ar: "نقرة", en: "Clicks", fr: "Clics" })} />
        <StatBox icon={Users} value={stats.estimated_bookings} label={L({ ar: "حجز تقديري", en: "Est. bookings", fr: "Rés. estimées" })} />
        <StatBox icon={DollarSign} value={`$${stats.estimated_commission_usd}`} label={L({ ar: "عمولة", en: "Commission", fr: "Commission" })} highlight />
      </div>

      <div className="rounded-2xl bg-[#F3EDE4] p-3 flex items-center gap-2">
        <div className="flex-1 truncate text-xs text-[#1C1917]" data-testid="referral-url">{refUrl}</div>
        <button
          onClick={copy}
          data-testid="referral-copy"
          className="h-9 w-9 rounded-full bg-[#1C1917] text-[#FAF7F2] grid place-items-center hover:bg-[#C85A32] transition-colors"
        >
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
        </button>
      </div>

      <SocialShare url={refUrl} text={shareText} testidPrefix="referral-share" />
    </div>
  );
}

function StatBox({ icon: Icon, value, label, highlight }) {
  return (
    <div className={`p-3 rounded-2xl ${highlight ? "bg-[#1C1917] text-[#FAF7F2]" : "bg-[#F3EDE4] text-[#1C1917]"}`}>
      <Icon className={`h-4 w-4 ${highlight ? "text-[#D97706]" : "text-[#C85A32]"}`} />
      <div className="font-display text-xl mt-1">{value}</div>
      <div className="text-[10px] uppercase tracking-widest opacity-70">{label}</div>
    </div>
  );
}
